/**
 * محرك التخزين المزدوج (السحابي + المحلي) والمزامنة التلقائية في الخلفية.
 *
 * يضمن عمل التطبيق بكامل طاقته بوجود الإنترنت أو بدونه:
 * 1. حفظ نسخة محلية حية في IndexedDB لكل البيانات المقروءة من السحابة.
 * 2. القراءة الفورية من IndexedDB عند انقطاع الإنترنت أو ضعف الشبكة.
 * 3. تسجيل العمليات والتعديلات والصور محليًا فورًا عند انقطاع الإنترنت في طابور المزامنة (sync_queue).
 * 4. رفع ومزامنة الطابور تلقائيًا إلى السحابة فور عودة الاتصال بالإنترنت.
 */

import { newId } from '@/domain/ids';
import type { Attachment, Task, TaskAssignee, TaskStep } from '@/domain/types';
import { buildStoragePath, sha256Hex } from './blob-store';
import { getLocalDb, type FileBlob, type SyncQueueEntry, type SyncQueueOperation } from './local/db';
import type {
  ApproveTaskInput,
  CompleteTaskInput,
  CreateTaskCommandInput,
  Filter,
  FilterOperator,
  QueryOptions,
  Row,
  TableName,
  UpdateTaskCommandInput,
  UploadAttachmentInput,
} from './types';

type StoredRow = Record<string, unknown> & { id: string };

export interface SyncStatusSnapshot {
  online: boolean;
  pendingCount: number;
  syncing: boolean;
  lastSyncedAt: string | null;
}

type SyncStatusListener = (status: SyncStatusSnapshot) => void;

const listeners = new Set<SyncStatusListener>();
let currentStatus: SyncStatusSnapshot = {
  online: typeof navigator !== 'undefined' ? navigator.onLine !== false : true,
  pendingCount: 0,
  syncing: false,
  lastSyncedAt: null,
};

function notifyListeners() {
  const snapshot = { ...currentStatus };
  for (const listener of listeners) {
    try {
      listener(snapshot);
    } catch {
      // تجاهل أخطاء المستمعين
    }
  }
}

export function getSyncStatus(): SyncStatusSnapshot {
  return { ...currentStatus };
}

export function subscribeSyncStatus(listener: SyncStatusListener): () => void {
  listeners.add(listener);
  listener({ ...currentStatus });
  void refreshPendingCount();
  return () => {
    listeners.delete(listener);
  };
}

export function isBrowserOnline(): boolean {
  if (typeof navigator === 'undefined') return true;
  return navigator.onLine !== false;
}

export function isNetworkError(error: unknown): boolean {
  if (!isBrowserOnline()) return true;
  if (!error) return false;
  if (typeof error === 'object' && 'code' in error && (error as { code?: unknown }).code === 'network') {
    return true;
  }
  const msg = error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase();
  return (
    msg.includes('fetch') ||
    msg.includes('network') ||
    msg.includes('failed to fetch') ||
    msg.includes('offline') ||
    msg.includes('تعذّر الاتصال')
  );
}

/* ============================================================================
 * 1) المرآة المحلية (IndexedDB Read / Write Mirror)
 * ========================================================================== */

export async function mirrorRowsToLocal<T extends TableName>(table: T, rows: Row<T>[]): Promise<void> {
  if (typeof indexedDB === 'undefined' || rows.length === 0) return;
  try {
    const db = await getLocalDb();
    const tx = db.transaction(table, 'readwrite');
    for (const row of rows) {
      if (row && typeof (row as { id?: unknown }).id === 'string') {
        void tx.store.put(row as unknown as StoredRow);
      }
    }
    await tx.done;
  } catch {
    // لا نوقف عمل التطبيق إن تعذر التخزين المحلي المؤقت
  }
}

export async function mirrorRowToLocal<T extends TableName>(table: T, row: Row<T> | null | undefined): Promise<void> {
  if (typeof indexedDB === 'undefined' || !row || typeof (row as { id?: unknown }).id !== 'string') return;
  try {
    const db = await getLocalDb();
    await db.put(table, row as unknown as StoredRow);
  } catch {
    // تجاهل
  }
}

export async function removeRowFromLocal<T extends TableName>(table: T, id: string): Promise<void> {
  if (typeof indexedDB === 'undefined' || !id) return;
  try {
    const db = await getLocalDb();
    await db.delete(table, id);
  } catch {
    // تجاهل
  }
}

function compareValues(a: unknown, b: unknown): number {
  if (a === b) return 0;
  if (a === null || a === undefined) return -1;
  if (b === null || b === undefined) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  const aStr = String(a);
  const bStr = String(b);
  return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
}

function matchesLocalFilter(row: StoredRow, filter: Filter<StoredRow> | undefined): boolean {
  if (!filter) return true;
  for (const [field, operator] of Object.entries(filter) as Array<[string, FilterOperator]>) {
    if (!operator) continue;
    const value = row[field];
    switch (operator.op) {
      case 'eq':
        if (value !== operator.value) return false;
        break;
      case 'neq':
        if (value === operator.value) return false;
        break;
      case 'in':
        if (!operator.value.includes(value)) return false;
        break;
      case 'isNull':
        if (value !== null && value !== undefined) return false;
        break;
      case 'isNotNull':
        if (value === null || value === undefined) return false;
        break;
      case 'gt':
        if (!(compareValues(value, operator.value) > 0)) return false;
        break;
      case 'gte':
        if (!(compareValues(value, operator.value) >= 0)) return false;
        break;
      case 'lt':
        if (!(compareValues(value, operator.value) < 0)) return false;
        break;
      case 'lte':
        if (!(compareValues(value, operator.value) <= 0)) return false;
        break;
      case 'contains':
        if (!Array.isArray(value) || !operator.value.some((v) => value.includes(v))) return false;
        break;
      default:
        return false;
    }
  }
  return true;
}

function applyLocalOrder(rows: StoredRow[], orderBy: QueryOptions<StoredRow>['orderBy']): StoredRow[] {
  if (!orderBy || orderBy.length === 0) return rows;
  return [...rows].sort((a, b) => {
    for (const order of orderBy) {
      const result = compareValues(a[order.field], b[order.field]);
      if (result !== 0) return order.direction === 'asc' ? result : -result;
    }
    return 0;
  });
}

export async function readLocalList<T extends TableName>(
  table: T,
  options: QueryOptions<Row<T>> = {},
): Promise<Row<T>[]> {
  if (typeof indexedDB === 'undefined') return [];
  try {
    const db = await getLocalDb();
    const all = (await db.getAll(table)) as StoredRow[];
    const filtered = all.filter((row) =>
      matchesLocalFilter(row, options.filter as Filter<StoredRow> | undefined),
    );
    const ordered = applyLocalOrder(filtered, options.orderBy as QueryOptions<StoredRow>['orderBy']);
    const start = options.offset ?? 0;
    const end = options.limit !== undefined ? start + options.limit : undefined;
    return ordered.slice(start, end) as unknown as Row<T>[];
  } catch {
    return [];
  }
}

export async function readLocalQuery<T extends TableName>(
  table: T,
  options: QueryOptions<Row<T>> = {},
): Promise<{ rows: Row<T>[]; total: number | null }> {
  if (typeof indexedDB === 'undefined') return { rows: [], total: 0 };
  try {
    const db = await getLocalDb();
    const all = (await db.getAll(table)) as StoredRow[];
    const filtered = all.filter((row) =>
      matchesLocalFilter(row, options.filter as Filter<StoredRow> | undefined),
    );
    const ordered = applyLocalOrder(filtered, options.orderBy as QueryOptions<StoredRow>['orderBy']);
    const start = options.offset ?? 0;
    const end = options.limit !== undefined ? start + options.limit : undefined;
    return {
      rows: ordered.slice(start, end) as unknown as Row<T>[],
      total: filtered.length,
    };
  } catch {
    return { rows: [], total: 0 };
  }
}

export async function readLocalGet<T extends TableName>(table: T, id: string): Promise<Row<T> | null> {
  if (typeof indexedDB === 'undefined' || !id) return null;
  try {
    const db = await getLocalDb();
    const row = await db.get(table, id);
    return (row as unknown as Row<T>) ?? null;
  } catch {
    return null;
  }
}

export async function readLocalCount<T extends TableName>(
  table: T,
  filter?: Filter<Row<T>>,
): Promise<number> {
  if (typeof indexedDB === 'undefined') return 0;
  try {
    const db = await getLocalDb();
    const all = (await db.getAll(table)) as StoredRow[];
    if (!filter) return all.length;
    return all.filter((row) => matchesLocalFilter(row, filter as Filter<StoredRow>)).length;
  } catch {
    return 0;
  }
}

/* ============================================================================
 * 2) تخزين ملفات الوسائط محليًا (Offline Blob Store)
 * ========================================================================== */

const localBlobUrls = new Map<string, string>();

export async function saveLocalBlob(storagePath: string, bytes: ArrayBuffer, mimeType: string): Promise<void> {
  if (typeof indexedDB === 'undefined') return;
  try {
    const db = await getLocalDb();
    const existing = (await db.getFromIndex('file_blobs', 'storagePath', storagePath)) as FileBlob | undefined;
    await db.put('file_blobs', {
      id: existing?.id ?? newId(),
      storagePath,
      bytes,
      mimeType,
      updatedAt: new Date().toISOString(),
    });
  } catch {
    // تجاهل
  }
}

export async function getLocalBlobBytes(storagePath: string): Promise<{ bytes: ArrayBuffer; mimeType: string } | null> {
  if (typeof indexedDB === 'undefined') return null;
  try {
    const db = await getLocalDb();
    const record = (await db.getFromIndex('file_blobs', 'storagePath', storagePath)) as FileBlob | undefined;
    if (!record) return null;
    return { bytes: record.bytes, mimeType: record.mimeType };
  } catch {
    return null;
  }
}

export async function getLocalBlobUrl(storagePath: string): Promise<string | null> {
  const cachedUrl = localBlobUrls.get(storagePath);
  if (cachedUrl) return cachedUrl;
  const record = await getLocalBlobBytes(storagePath);
  if (!record) return null;
  const blob = new Blob([record.bytes], { type: record.mimeType || 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  localBlobUrls.set(storagePath, url);
  return url;
}

export async function removeLocalBlob(storagePath: string): Promise<void> {
  if (typeof indexedDB === 'undefined') return;
  try {
    const cachedUrl = localBlobUrls.get(storagePath);
    if (cachedUrl) {
      URL.revokeObjectURL(cachedUrl);
      localBlobUrls.delete(storagePath);
    }
    const db = await getLocalDb();
    const record = (await db.getFromIndex('file_blobs', 'storagePath', storagePath)) as FileBlob | undefined;
    if (record) await db.delete('file_blobs', record.id);
  } catch {
    // تجاهل
  }
}

/* ============================================================================
 * 3) طابور المزامنة التلقائية (Offline Mutation Queue)
 * ========================================================================== */

export async function refreshPendingCount(): Promise<number> {
  if (typeof indexedDB === 'undefined') return 0;
  try {
    const db = await getLocalDb();
    const count = await db.count('sync_queue');
    if (currentStatus.pendingCount !== count) {
      currentStatus.pendingCount = count;
      notifyListeners();
    }
    return count;
  } catch {
    return 0;
  }
}

export async function enqueueSyncOperation(operation: SyncQueueOperation, payload: unknown): Promise<void> {
  if (typeof indexedDB === 'undefined') return;
  try {
    const db = await getLocalDb();
    const now = new Date().toISOString();
    const entry: SyncQueueEntry = {
      id: newId(),
      operation,
      payload,
      attempts: 0,
      lastError: null,
      createdAt: now,
      updatedAt: now,
    };
    await db.put('sync_queue', entry);
    await refreshPendingCount();
  } catch (err) {
    console.warn('Failed to enqueue offline operation', err);
  }
}

/* ============================================================================
 * 4) تنفيذ الأوامر محليًا أثناء انقطاع الإنترنت (Offline Command Fallbacks)
 * ========================================================================== */

export async function offlineCreateTask(
  input: CreateTaskCommandInput,
  companyId: string,
): Promise<Task> {
  const now = new Date().toISOString();
  const taskId = newId();
  const task: Task = {
    id: taskId,
    companyId,
    brandId: input.task.brandId,
    branchId: input.task.branchId,
    categoryId: input.task.categoryId,
    templateId: null,
    occurrenceKey: taskId,
    title: input.task.title,
    description: input.task.description,
    instructions: input.task.instructions,
    taskType: input.task.taskType,
    priority: input.task.priority,
    status: 'pending',
    approvalState: input.task.requiresApproval ? 'pending' : 'not_required',
    assignmentMode: input.task.assignmentMode,
    completionRule: input.task.completionRule,
    requiresProof: input.task.requiresProof,
    requiresApproval: input.task.requiresApproval,
    approverMembershipId: input.task.approverMembershipId,
    createdByMembershipId: input.actorMembershipId,
    createdByUserId: input.actorUserId,
    isPersonal: input.task.isPersonal,
    visibility: input.task.visibility,
    assigneeScope: {
      branchIds: input.task.branchId ? [input.task.branchId] : [],
      brandIds: input.task.brandId ? [input.task.brandId] : [],
      membershipIds: input.task.assigneeMembershipIds,
      includeSupervised: false,
    },
    startAt: input.task.startAt,
    dueAt: input.task.dueAt,
    completedAt: null,
    approvedAt: null,
    approvedByMembershipId: null,
    returnedAt: null,
    returnedByMembershipId: null,
    returnedReason: null,
    cancelledAt: null,
    cancelledByMembershipId: null,
    localDate: input.task.localDate,
    timeZone: input.task.timeZone,
    version: 1,
    createdAt: now,
    updatedAt: now,
    deletedAt: null,
  };

  await mirrorRowToLocal('tasks', task);

  const assignees: TaskAssignee[] = input.task.assigneeMembershipIds.map((membershipId) => ({
    id: newId(),
    taskId,
    membershipId,
    state: 'pending',
    assignedAt: now,
    assignedByMembershipId: input.actorMembershipId,
    unassignedAt: null,
    unassignedReason: null,
    completedAt: null,
    completedByMembershipId: null,
    proofNote: null,
  }));
  await mirrorRowsToLocal('task_assignees', assignees);

  if (input.steps && input.steps.length > 0) {
    const steps: TaskStep[] = input.steps.map((title, idx) => ({
      id: newId(),
      taskId,
      title,
      sortOrder: idx + 1,
      isDone: false,
      doneByMembershipId: null,
      doneAt: null,
    }));
    await mirrorRowsToLocal('task_steps', steps);
  }

  await enqueueSyncOperation('task.create', { input, localTaskId: taskId });
  return task;
}

export async function offlineUpdateTask(input: UpdateTaskCommandInput): Promise<Task> {
  const existing = await readLocalGet('tasks', input.taskId);
  const now = new Date().toISOString();
  const updated: Task = {
    ...(existing as Task),
    ...input.patch,
    id: input.taskId,
    updatedAt: now,
    version: ((existing as Task | null)?.version ?? 1) + 1,
  };
  await mirrorRowToLocal('tasks', updated);
  await enqueueSyncOperation('task.update', input);
  return updated;
}

export async function offlineCompleteTask(input: CompleteTaskInput): Promise<Task> {
  const existing = await readLocalGet('tasks', input.taskId);
  const now = new Date().toISOString();
  const assignees = await readLocalList('task_assignees', {
    filter: { taskId: { op: 'eq', value: input.taskId } },
  });
  for (const a of assignees) {
    if (!input.membershipId || a.membershipId === input.membershipId) {
      await mirrorRowToLocal('task_assignees', {
        ...a,
        state: 'completed',
        completedAt: now,
        completedByMembershipId: input.membershipId ?? a.membershipId,
        proofNote: input.proofNote,
      });
    }
  }
  const updated: Task = {
    ...(existing as Task),
    id: input.taskId,
    status: 'completed',
    completedAt: now,
    approvalState: (existing as Task | null)?.requiresApproval ? 'pending' : 'not_required',
    updatedAt: now,
    version: ((existing as Task | null)?.version ?? 1) + 1,
  };
  await mirrorRowToLocal('tasks', updated);
  await enqueueSyncOperation('task.complete', input);
  return updated;
}

export async function offlineDecideApproval(input: ApproveTaskInput): Promise<Task> {
  const existing = await readLocalGet('tasks', input.taskId);
  const now = new Date().toISOString();
  const isApproved = input.decision === 'approve';
  const updated: Task = {
    ...(existing as Task),
    id: input.taskId,
    status: isApproved ? 'completed' : 'in_progress',
    approvalState: isApproved ? 'approved' : 'returned',
    approvedAt: isApproved ? now : null,
    approvedByMembershipId: isApproved ? input.membershipId : null,
    returnedAt: !isApproved ? now : null,
    returnedByMembershipId: !isApproved ? input.membershipId : null,
    returnedReason: !isApproved ? input.reason : null,
    updatedAt: now,
    version: ((existing as Task | null)?.version ?? 1) + 1,
  };
  await mirrorRowToLocal('tasks', updated);
  await enqueueSyncOperation('task.decideApproval', input);
  return updated;
}

export async function offlineReopenTask(input: {
  taskId: string;
  membershipId: string | null;
  actorUserId: string;
  reason: string | null;
}): Promise<Task> {
  const existing = await readLocalGet('tasks', input.taskId);
  const now = new Date().toISOString();
  const updated: Task = {
    ...(existing as Task),
    id: input.taskId,
    status: 'in_progress',
    completedAt: null,
    updatedAt: now,
    version: ((existing as Task | null)?.version ?? 1) + 1,
  };
  await mirrorRowToLocal('tasks', updated);
  await enqueueSyncOperation('task.reopen', input);
  return updated;
}

export async function offlineCancelTask(input: {
  taskId: string;
  membershipId: string | null;
  actorUserId: string;
  reason: string | null;
}): Promise<Task> {
  const existing = await readLocalGet('tasks', input.taskId);
  const now = new Date().toISOString();
  const updated: Task = {
    ...(existing as Task),
    id: input.taskId,
    status: 'cancelled',
    cancelledAt: now,
    cancelledByMembershipId: input.membershipId,
    updatedAt: now,
    version: ((existing as Task | null)?.version ?? 1) + 1,
  };
  await mirrorRowToLocal('tasks', updated);
  await enqueueSyncOperation('task.cancel', input);
  return updated;
}

export async function offlineDeleteTask(input: { taskId: string; actorUserId: string }): Promise<void> {
  await removeRowFromLocal('tasks', input.taskId);
  await enqueueSyncOperation('task.delete', input);
}

export async function offlineUploadAttachment(
  input: UploadAttachmentInput,
  userId: string,
): Promise<Attachment> {
  const attachmentId = newId();
  const storagePath = buildStoragePath({
    companyId: input.companyId,
    taskId: input.taskId,
    attachmentId,
    fileName: input.fileName,
  });

  await saveLocalBlob(storagePath, input.bytes, input.mimeType);
  const checksum = await sha256Hex(input.bytes);
  const now = new Date().toISOString();

  const attachment: Attachment = {
    id: attachmentId,
    companyId: input.companyId,
    taskId: input.taskId,
    taskAssigneeId: input.taskAssigneeId,
    storagePath,
    fileName: input.fileName,
    mimeType: input.mimeType,
    sizeBytes: input.bytes.byteLength,
    kind: input.kind,
    uploadState: 'uploaded',
    uploadedByUserId: userId,
    checksum,
    clientRef: input.clientRef,
    retentionUntil: null,
    deletedAt: null,
    deletionId: null,
    createdAt: now,
    updatedAt: now,
  };

  await mirrorRowToLocal('attachments', attachment);
  await enqueueSyncOperation('attachment.upload', {
    attachment,
    storagePath,
    companyId: input.companyId,
    taskId: input.taskId,
    taskAssigneeId: input.taskAssigneeId,
    fileName: input.fileName,
    mimeType: input.mimeType,
    kind: input.kind,
    uploadedByUserId: userId,
    clientRef: input.clientRef,
  });

  return attachment;
}

export async function offlineDeleteAttachment(input: {
  attachmentId: string;
  actorUserId: string;
  mode: 'manual';
}): Promise<void> {
  const existing = await readLocalGet('attachments', input.attachmentId);
  if (existing) {
    await removeLocalBlob(existing.storagePath);
    await removeRowFromLocal('attachments', input.attachmentId);
  }
  await enqueueSyncOperation('attachment.delete', input);
}

/* ============================================================================
 * 5) المعالج التلقائي لطابور المزامنة عند عودة الإنترنت
 * ========================================================================== */

export interface OnlineSyncExecutor {
  insertRow(table: TableName, row: StoredRow): Promise<unknown>;
  insertManyRows(table: TableName, rows: StoredRow[]): Promise<unknown>;
  updateRow(table: TableName, id: string, patch: Partial<StoredRow>): Promise<unknown>;
  removeRow(table: TableName, id: string): Promise<void>;
  createTask(input: CreateTaskCommandInput): Promise<Task>;
  updateTask(input: UpdateTaskCommandInput): Promise<Task>;
  completeTask(input: CompleteTaskInput): Promise<Task>;
  decideApproval(input: ApproveTaskInput): Promise<Task>;
  reopenTask(input: { taskId: string; membershipId: string | null; actorUserId: string; reason: string | null }): Promise<Task>;
  cancelTask(input: { taskId: string; membershipId: string | null; actorUserId: string; reason: string | null }): Promise<Task>;
  deleteTask(input: { taskId: string; actorUserId: string }): Promise<void>;
  uploadAttachmentWithRecord(attachment: Attachment, bytes: ArrayBuffer): Promise<Attachment>;
  deleteAttachment(input: { attachmentId: string; actorUserId: string; mode: 'manual' }): Promise<void>;
}

let activeExecutor: OnlineSyncExecutor | null = null;
let syncInProgress = false;
let listenersBound = false;

export function registerOnlineSyncExecutor(executor: OnlineSyncExecutor): void {
  activeExecutor = executor;
  bindNetworkListeners();
  void refreshPendingCount().then((count) => {
    if (count > 0 && isBrowserOnline()) {
      void flushSyncQueue();
    }
  });
}

function bindNetworkListeners() {
  if (listenersBound || typeof window === 'undefined') return;
  listenersBound = true;

  window.addEventListener('online', () => {
    currentStatus.online = true;
    notifyListeners();
    void flushSyncQueue();
  });

  window.addEventListener('offline', () => {
    currentStatus.online = false;
    notifyListeners();
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && isBrowserOnline()) {
      void flushSyncQueue();
    }
  });

  setInterval(() => {
    if (isBrowserOnline() && currentStatus.pendingCount > 0 && !syncInProgress) {
      void flushSyncQueue();
    }
  }, 12_000);
}

export async function flushSyncQueue(): Promise<{ synced: number; remaining: number }> {
  if (syncInProgress || !activeExecutor || !isBrowserOnline() || typeof indexedDB === 'undefined') {
    return { synced: 0, remaining: currentStatus.pendingCount };
  }

  syncInProgress = true;
  currentStatus.syncing = true;
  notifyListeners();

  let synced = 0;
  try {
    const db = await getLocalDb();
    const entries = await db.getAll('sync_queue');
    entries.sort((a, b) => (a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0));

    // خريطة لربط المعرفات المؤقتة للمهام التي أُنشئت بدون إنترنت بالمعرفات السحابية الحقيقية
    const taskIdMap = new Map<string, string>();

    for (const entry of entries) {
      if (!isBrowserOnline()) break;
      try {
        await replaySingleEntry(activeExecutor, entry, taskIdMap);
        await db.delete('sync_queue', entry.id);
        synced += 1;
      } catch (error) {
        if (isNetworkError(error)) {
          // توقف مؤقت حتى يستقر الإنترنت
          break;
        }
        // إذا تكرر الخطأ غير الشبكي أكثر من 5 مرات، نحذفه لئلا يعطل بقية الطابور
        const nextAttempts = (entry.attempts ?? 0) + 1;
        if (nextAttempts >= 5) {
          await db.delete('sync_queue', entry.id);
        } else {
          await db.put('sync_queue', {
            ...entry,
            attempts: nextAttempts,
            lastError: error instanceof Error ? error.message : String(error),
            updatedAt: new Date().toISOString(),
          });
        }
      }
    }
  } finally {
    syncInProgress = false;
    currentStatus.syncing = false;
    if (synced > 0) {
      currentStatus.lastSyncedAt = new Date().toISOString();
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('taskflow:sync-completed', { detail: { synced } }));
      }
    }
    await refreshPendingCount();
    notifyListeners();
  }

  return { synced, remaining: currentStatus.pendingCount };
}

async function replaySingleEntry(
  executor: OnlineSyncExecutor,
  entry: SyncQueueEntry,
  taskIdMap: Map<string, string>,
): Promise<void> {
  const payload = entry.payload as Record<string, unknown>;

  switch (entry.operation) {
    case 'table.insert': {
      const table = payload.table as TableName;
      const row = { ...(payload.row as StoredRow) };
      if (typeof row.taskId === 'string' && taskIdMap.has(row.taskId)) {
        row.taskId = taskIdMap.get(row.taskId)!;
      }
      await executor.insertRow(table, row);
      break;
    }
    case 'table.insertMany': {
      const table = payload.table as TableName;
      const rows = (payload.rows as StoredRow[]).map((r) => {
        const copy = { ...r };
        if (typeof copy.taskId === 'string' && taskIdMap.has(copy.taskId)) {
          copy.taskId = taskIdMap.get(copy.taskId)!;
        }
        return copy;
      });
      await executor.insertManyRows(table, rows);
      break;
    }
    case 'table.update': {
      const table = payload.table as TableName;
      const id = (payload.id as string) ?? '';
      const resolvedId = taskIdMap.get(id) ?? id;
      const patch = payload.patch as Partial<StoredRow>;
      await executor.updateRow(table, resolvedId, patch);
      break;
    }
    case 'table.remove': {
      const table = payload.table as TableName;
      const id = (payload.id as string) ?? '';
      const resolvedId = taskIdMap.get(id) ?? id;
      await executor.removeRow(table, resolvedId);
      break;
    }
    case 'task.create': {
      const input = payload.input as CreateTaskCommandInput;
      const localTaskId = payload.localTaskId as string | undefined;
      const created = await executor.createTask(input);
      if (localTaskId && created.id !== localTaskId) {
        taskIdMap.set(localTaskId, created.id);
        await removeRowFromLocal('tasks', localTaskId);
      }
      await mirrorRowToLocal('tasks', created);
      break;
    }
    case 'task.update': {
      const input = payload as unknown as UpdateTaskCommandInput;
      const resolvedId = taskIdMap.get(input.taskId) ?? input.taskId;
      await executor.updateTask({ ...input, taskId: resolvedId });
      break;
    }
    case 'task.complete': {
      const input = payload as unknown as CompleteTaskInput;
      const resolvedId = taskIdMap.get(input.taskId) ?? input.taskId;
      await executor.completeTask({ ...input, taskId: resolvedId });
      break;
    }
    case 'task.decideApproval':
    case 'task.approve':
    case 'task.return': {
      const input = payload as unknown as ApproveTaskInput;
      const resolvedId = taskIdMap.get(input.taskId) ?? input.taskId;
      await executor.decideApproval({ ...input, taskId: resolvedId });
      break;
    }
    case 'task.reopen': {
      const input = payload as { taskId: string; membershipId: string | null; actorUserId: string; reason: string | null };
      const resolvedId = taskIdMap.get(input.taskId) ?? input.taskId;
      await executor.reopenTask({ ...input, taskId: resolvedId });
      break;
    }
    case 'task.cancel': {
      const input = payload as { taskId: string; membershipId: string | null; actorUserId: string; reason: string | null };
      const resolvedId = taskIdMap.get(input.taskId) ?? input.taskId;
      await executor.cancelTask({ ...input, taskId: resolvedId });
      break;
    }
    case 'task.delete': {
      const input = payload as { taskId: string; actorUserId: string };
      const resolvedId = taskIdMap.get(input.taskId) ?? input.taskId;
      await executor.deleteTask({ ...input, taskId: resolvedId });
      break;
    }
    case 'attachment.upload': {
      const attachment = payload.attachment as Attachment;
      const storagePath = payload.storagePath as string;
      const blobRecord = await getLocalBlobBytes(storagePath);
      if (!blobRecord) break;
      const resolvedTaskId =
        attachment.taskId && taskIdMap.has(attachment.taskId)
          ? taskIdMap.get(attachment.taskId)!
          : attachment.taskId;
      await executor.uploadAttachmentWithRecord(
        { ...attachment, taskId: resolvedTaskId },
        blobRecord.bytes,
      );
      break;
    }
    case 'attachment.delete': {
      const input = payload as { attachmentId: string; actorUserId: string; mode: 'manual' };
      await executor.deleteAttachment(input);
      break;
    }
    default:
      break;
  }
}
